/**
 * Deploy identity shared by both shells: which commit this bundle was built
 * from, which commit the origin serves now, and whether an error means an app
 * chunk from an older deploy is gone. No imports, so the new shell can use it
 * without the legacy environment config (`registerVersionDetection` in
 * `./versionDetection.ts` adds the legacy polling and window listeners).
 */

export interface ErrorWithMessage {
  message?: string
  name?: string
}

const VERSION_CHECK_TIMEOUT_MS = 10_000

/** The commit DefinePlugin baked into this bundle, or null for a local build. */
export const bundleCommit = (): string | null => {
  const commit = process.env.VERCEL_GIT_COMMIT_SHA
  return commit && commit !== 'N/A' ? commit : null
}

/**
 * The commit an HTML document names: both templates render
 * `<meta name="vercel-git-commit-sha">`; the second pattern reads documents
 * built before that tag existed.
 */
export const readDeployedCommit = (html: string): string | null => {
  const metaTagMatch = html.match(
    /<meta\s+name=["']vercel-git-commit-sha["']\s+content=["']([^"']+)["']/
  )
  const oldFormatMatch = html.match(
    /VERCEL_GIT_COMMIT_SHA['"]\s*:\s*['"]([^'"]+)['"]/
  )
  const commit = metaTagMatch?.[1] ?? oldFormatMatch?.[1]
  return commit && commit !== 'N/A' ? commit : null
}

/**
 * The commit the origin serves now, read from a fresh `/index.html`. Null when
 * the request fails, times out, or the document names no commit: a version
 * check never treats a network problem as a new deploy.
 */
export const fetchDeployedCommit = async (): Promise<string | null> => {
  // AbortController rather than `AbortSignal.timeout`, which Safari 15 (module
  // workers, so inside the supported range) lacks.
  const controller = new AbortController()
  const timeout = setTimeout(() => controller.abort(), VERSION_CHECK_TIMEOUT_MS)
  try {
    const response = await fetch(`${window.location.origin}/index.html`, {
      cache: 'no-store',
      headers: { 'Cache-Control': 'no-cache' },
      signal: controller.signal,
    })
    if (!response.ok) return null
    return readDeployedCommit(await response.text())
  } catch {
    return null
  } finally {
    clearTimeout(timeout)
  }
}

export const isChunkLoadError = (error?: ErrorWithMessage | null): boolean => {
  const message = error?.message?.toLowerCase() ?? ''
  const name = error?.name?.toLowerCase() ?? ''

  // Match ONLY failures that are unambiguously about loading an app chunk.
  // We deliberately do NOT match a bare "failed to fetch": that is the browser's
  // generic message for *any* failed fetch() — a dead/slow API, CORS, offline, an
  // expired session, an embedded-iframe 400, etc. Matching it caused an infinite
  // reload loop: a routine API network error was read as "new version available",
  // so the update banner fired; clicking Reload never fixed the underlying failing
  // request, so it fired again, forever. A genuine dynamic-import (chunk) failure
  // instead surfaces as a ChunkLoadError (webpack/rspack) or one of the specific
  // module-loading messages below (native ESM across Chrome/Firefox/Safari).
  return (
    name === 'chunkloaderror' ||
    message.includes('loading chunk') ||
    message.includes('loading css chunk') ||
    message.includes('failed to fetch dynamically imported module') ||
    message.includes('error loading dynamically imported module') ||
    message.includes('importing a module script failed')
  )
}
